import { CommonModule } from "@angular/common";
import { Component, Inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import {
	MAT_DIALOG_DATA,
	MatDialogModule,
	MatDialogRef,
} from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Subject, takeUntil } from "rxjs";

import { NotificationService } from "src/app/_core/providers/notification.service";
import { ContactService } from "src/app/modules/contacts/services/contact.service";
import { PuestoDto } from "src/app/modules/contacts/interfaces/contact.interface";
import { DsSelectComponent } from "src/app/modules/form-controls/components/ds-select/ds-select.component";
import { DsToggleComponent } from "src/app/modules/form-controls/components/ds-toggle/ds-toggle.component";

export interface PositionDialogData {
	/** Flujo del tab de origen: cada ámbito tiene su propio diálogo (no se mezclan). */
	mode: "general" | "funcion";
	/** Rol a editar; null/undefined = crear. */
	puesto?: PuestoDto | null;
	/** Catálogo de funciones (solo modo "funcion"). */
	funciones: string[];
	/** Función preseleccionada al crear desde el tab por función. */
	funcionDefault?: string | null;
}

/**
 * Diálogo de alta/edición de un ROL de persona (ADR-0005), espejo visual del diálogo
 * integral de Persona (`ds-dialog is-complex` + overlay `ds-dialog-panel`). El ámbito
 * (general vs particular) viene FIJADO por el tab desde el que se abre: aquí no se
 * cambia, para que cada flujo se entienda solo. Devuelve `true` si se guardó.
 */
@Component({
	selector: "app-position-dialog",
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		MatDialogModule,
		MatIconModule,
		TranslateModule,
		DsSelectComponent,
		DsToggleComponent,
	],
	templateUrl: "./position-dialog.component.html",
	styleUrls: ["./position-dialog.component.scss"],
})
export class PositionDialogComponent {
	private readonly destroy$ = new Subject<void>();

	readonly isNew: boolean;
	isSaving = false;

	codigo: string;
	description: string;
	funcionAgregada: string | null;
	activo: boolean;

	funcionLabelOf = (f: string | null): string => f ?? "";

	constructor(
		public readonly dialogRef: MatDialogRef<PositionDialogComponent, boolean>,
		@Inject(MAT_DIALOG_DATA) public readonly data: PositionDialogData,
		private readonly contactService: ContactService,
		private readonly notificationService: NotificationService,
		private readonly translateService: TranslateService,
	) {
		const p = data.puesto;
		this.isNew = !p;
		this.codigo = p?.codigo ?? "";
		this.description = p?.description ?? "";
		this.funcionAgregada =
			data.mode === "funcion"
				? (p?.funcionAgregada ?? data.funcionDefault ?? null)
				: null;
		this.activo = p?.activo !== false;
	}

	get isGeneral(): boolean {
		return this.data.mode === "general";
	}

	get canSave(): boolean {
		return (
			!!this.codigo?.trim() &&
			!!this.description?.trim() &&
			(this.isGeneral || !!this.funcionAgregada)
		);
	}

	close(): void {
		this.dialogRef.close(false);
	}

	save(): void {
		if (!this.canSave || this.isSaving) {
			return;
		}
		this.isSaving = true;

		const payload: PuestoDto = {
			id: this.data.puesto?.id ?? 0,
			codigo: this.codigo.trim(),
			description: this.description.trim(),
			esGeneral: this.isGeneral,
			funcionAgregada: this.isGeneral ? null : this.funcionAgregada,
			activo: this.activo,
		} as PuestoDto;

		const request$ = this.isNew
			? this.contactService.createPuesto(payload)
			: this.contactService.updatePuesto(payload);

		request$.pipe(takeUntil(this.destroy$)).subscribe({
			next: () => {
				this.notificationService.showSuccess(
					this.translateService.instant("positions.saved"),
					null,
				);
				this.dialogRef.close(true);
			},
			error: (err) => {
				this.isSaving = false;
				this.notificationService.showError(
					err?.error?.detail ||
						this.translateService.instant("positions.saveError"),
				);
			},
		});
	}
}
